import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { api, servicesApi } from "@/lib/api";
import {
  selectDisplayPackage,
  unwrapServiceResponse,
  type Package,
} from "@/app/components/PricingBar";
import { createPortal } from "react-dom";

const EMPTY_PACKAGES: Package[] = [];
import { useOrdersContext } from "@/context/OrdersContext";
import Modal from "@/app/components/ui/Modal";
import {
  ShieldCheck,
  Info,
  Shield,
  Building,
  Plus,
  Check,
} from "lucide-react";
import { WorkspaceNameIconInput } from "@/app/components/WorkspaceNameIconInput";
import { WorkspaceAvatarDisplay } from "@/app/components/WorkspaceAvatarDisplay";
import {
  DEFAULT_WORKSPACE_AVATAR,
  resolveWorkspaceAvatarFromWorkspace,
  resolveWorkspaceImageDisplayUrl,
  resolveWorkspaceImageUrlForCreate,
  type WorkspaceAvatarSelection,
} from "@/lib/workspaceAvatar";
import { syncWorkspaceAvatarTags } from "@/lib/workspaceAvatarTags";
import { workspacesApi, Workspace, uploadApi } from "@/lib/api";
import { useWorkerLayout } from "@/hooks/useWorkerLayout";

function HireModalWorkspaceAvatar({
  workspace,
  size,
  iconSize,
}: {
  workspace: Workspace;
  size: number;
  iconSize: number;
}) {
  const avatar = useMemo(
    () =>
      resolveWorkspaceAvatarFromWorkspace({
        imageUrl: workspace.imageUrl,
        tags: workspace.tags,
        resolveImageUrl: resolveWorkspaceImageDisplayUrl,
      }),
    [workspace.imageUrl, workspace.tags],
  );

  return (
    <WorkspaceAvatarDisplay
      avatar={avatar}
      size={size}
      iconSize={iconSize}
      className="shrink-0 rounded-lg"
    />
  );
}

interface HireModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    orderId?: string,
    workspaceId?: string,
    workspaceName?: string,
  ) => void;
  workerData: {
    title: string;
    packageName?: string;
    price: string;
    capacity: string;
    description?: string;
    expertName?: string;
    expertTitle?: string;
    thumbnail?: string;
  };
  serviceId?: string;
  packageId?: string;
  workspaceId?: string;
  onWorkspaceCreated?: () => void;
  onExited?: () => void;
}

export default function HireModal({
  isOpen,
  onClose,
  onConfirm,
  workerData,
  serviceId,
  packageId,
  workspaceId,
  onWorkspaceCreated,
  onExited,
}: HireModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Payment method state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paymentMethod, setPaymentMethod] = useState<"free" | "qr" | "card">(
    "free",
  );
  // State for processing
  const [isProcessing, setIsProcessing] = useState(false);

  // Orders are used to filter selectable workspaces for this service
  const { fetchOrders, orders, loadingOrders } = useOrdersContext();
  const {
    workspaces: allWorkspaces,
    fetchWorkspaces,
    isLoadingWorkspaces,
  } = useWorkerLayout();

  const workspacesById = useMemo(
    () => new Map(allWorkspaces.map((w) => [w.id, w])),
    [allWorkspaces],
  );

  const enrichWorkspace = useCallback(
    (ws: Workspace): Workspace => {
      const full = workspacesById.get(ws.id);
      if (!full) return ws;
      return {
        ...ws,
        imageUrl: full.imageUrl ?? ws.imageUrl,
        tags: full.tags ?? ws.tags,
      };
    },
    [workspacesById],
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const router = useRouter();

  /** แพ็กเกจจาก API — ใช้ `price` / `dailyPrice` เหมือนข้อมูลใน PricingBar */
  const [fetchedPackages, setFetchedPackages] = useState<Package[] | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen || !serviceId) {
      setFetchedPackages(null);
      return;
    }
    let cancelled = false;
    setFetchedPackages(null);
    (async () => {
      try {
        const response = await servicesApi.getById(serviceId);
        const service = unwrapServiceResponse(response);
        const raw = service?.packages;
        if (!Array.isArray(raw)) {
          if (!cancelled) setFetchedPackages([]);
          return;
        }
        const mapped: Package[] = raw.map((pkg) => ({
          id: pkg.id,
          name: pkg.name,
          price: Number(pkg.price) || 0,
          dailyPrice:
            typeof pkg.dailyPrice === "number"
              ? pkg.dailyPrice
              : Number(pkg.dailyPrice) || 0,
          transactionPrice:
            typeof pkg.transactionPrice === "number"
              ? pkg.transactionPrice
              : Number(pkg.transactionPrice) || 0,
          capacity: String(pkg.capacity ?? "0"),
          displayOrder:
            typeof pkg.displayOrder === "number" ? pkg.displayOrder : 0,
        }));
        if (!cancelled) setFetchedPackages(mapped);
      } catch {
        if (!cancelled) setFetchedPackages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, serviceId]);

  const effectivePackages = fetchedPackages ?? EMPTY_PACKAGES;
  const currentPackage = useMemo(() => {
    if (effectivePackages.length === 0) return null;
    if (packageId) {
      const byId = effectivePackages.find((p) => p.id === packageId);
      if (byId) return byId;
    }
    return selectDisplayPackage(effectivePackages);
  }, [effectivePackages, packageId]);

  const packagePrice = currentPackage ? Number(currentPackage.price) || 0 : 0;
  const hiringFee = packagePrice;
  const dailyRetainer = currentPackage
    ? Number(currentPackage.dailyPrice ?? 0)
    : 0;
  const totalDueToday = hiringFee + dailyRetainer;

  const paidPackageRef = useMemo(
    () => effectivePackages.find((p) => (Number(p.price) || 0) > 0),
    [effectivePackages],
  );

  const strikethroughHiringFee =
    paidPackageRef && Number(paidPackageRef.price ?? 0) > 0
      ? Number(paidPackageRef.price).toFixed(2)
      : "";
  const showStrikethroughHiringFee =
    packagePrice === 0 &&
    paidPackageRef != null &&
    strikethroughHiringFee !== "";

  const strikethroughDailyValue =
    paidPackageRef && Number(paidPackageRef.dailyPrice ?? 0) > 0
      ? Number(paidPackageRef.dailyPrice).toFixed(2)
      : "";
  const showStrikethroughDailyPrice =
    packagePrice === 0 &&
    paidPackageRef != null &&
    strikethroughDailyValue !== "";

  const pricingLoading = Boolean(serviceId && fetchedPackages === null);
  const pricingReady =
    Boolean(serviceId) && fetchedPackages !== null && currentPackage !== null;

  // Workspaces state for this modal (either all or filtered)
  const [filteredWorkspaces, setFilteredWorkspaces] = useState<Workspace[]>([]);
  const [isLoadingFiltered, setIsLoadingFiltered] = useState(false);

  // Determine which workspaces to use.
  // IMPORTANT: If service-specific filtering returns an empty list, user must create a new workspace.
  const workspaces = serviceId ? filteredWorkspaces : allWorkspaces;

  // Workspaces that already hired THIS service should not be selectable.
  const hiredWorkspaceIdSet = useMemo(() => {
    const sid = serviceId?.trim();
    if (!sid) return new Set<string>();
    const set = new Set<string>();
    for (const o of orders ?? []) {
      if (!o || typeof o !== "object") continue;
      const order = o as {
        serviceId?: string;
        service?: { id?: string };
        workspaceId?: string;
        workspace?: { id?: string };
      };

      const orderServiceId = order.serviceId || order.service?.id;
      if (orderServiceId !== sid) continue;

      const wsId = order.workspaceId || order.workspace?.id;
      if (typeof wsId === "string" && wsId) set.add(wsId);
    }
    return set;
  }, [orders, serviceId]);

  // Ensure orders are loaded so workspace filtering is accurate.
  useEffect(() => {
    if (!isOpen) return;
    if (!serviceId) return;
    // Fetch all buyer orders (no workspace filter) so we can exclude already-hired workspaces.
    void fetchOrders?.("buyer");
  }, [isOpen, serviceId, fetchOrders]);

  const selectableWorkspaces = useMemo(
    () => (workspaces ?? []).filter((w) => !hiredWorkspaceIdSet.has(w.id)),
    [workspaces, hiredWorkspaceIdSet],
  );

  // If no selectable workspaces remain, user must create a new one.
  const mustCreateNewWorkspace = selectableWorkspaces.length === 0;

  // Workspace selection state
  // Simplified dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [workspaceAvatar, setWorkspaceAvatar] =
    useState<WorkspaceAvatarSelection>(DEFAULT_WORKSPACE_AVATAR);

  useEffect(() => {
    if (!isProcessing) return;
    setIsDropdownOpen(false);
  }, [isProcessing]);

  // Prevent auto-select from overriding a user's manual selection.
  const hasUserSelectedWorkspaceRef = useRef(false);
  // Track whether create-mode was entered automatically (no selectable workspaces).
  const wasAutoForcedCreateRef = useRef(false);

  const workspaceButtonRef = useRef<HTMLButtonElement | null>(null);
  const workspaceDropdownRef = useRef<HTMLDivElement | null>(null);
  const workspaceItemRefs = useRef(new Map<string, HTMLElement>());
  const hasScrolledToSelectedOnOpenRef = useRef(false);
  const [workspaceDropdownRect, setWorkspaceDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    hasUserSelectedWorkspaceRef.current = false;
    wasAutoForcedCreateRef.current = false;
  }, [isOpen, workspaceId]);

  useEffect(() => {
    if (!mounted) return;
    if (!isDropdownOpen) {
      setWorkspaceDropdownRect(null);
      return;
    }

    const updatePosition = () => {
      const btn = workspaceButtonRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();

      const margin = 16;
      const width = Math.max(0, r.width);
      const left = Math.min(
        Math.max(margin, r.left),
        Math.max(margin, window.innerWidth - margin - width),
      );

      setWorkspaceDropdownRect({
        top: r.bottom + 8,
        left,
        width,
      });
    };

    updatePosition();

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const btn = workspaceButtonRef.current;
      if (btn && btn.contains(target)) return;
      const dd = workspaceDropdownRef.current;
      if (dd && dd.contains(target)) return;
      setIsDropdownOpen(false);
    };

    const onScroll = (e: Event) => {
      const dd = workspaceDropdownRef.current;
      if (dd && e.target instanceof Node && dd.contains(e.target)) return;
      updatePosition();
    };

    const scrollOpts: AddEventListenerOptions = {
      passive: true,
      capture: true,
    };
    window.addEventListener("resize", updatePosition, { passive: true });
    window.addEventListener("scroll", onScroll, scrollOpts);
    document.addEventListener("pointerdown", onPointerDown, { capture: true });

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      } as EventListenerOptions);
    };
  }, [mounted, isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) {
      hasScrolledToSelectedOnOpenRef.current = false;
      return;
    }
    if (
      !selectedWorkspaceId ||
      !workspaceDropdownRect ||
      hasScrolledToSelectedOnOpenRef.current
    ) {
      return;
    }

    requestAnimationFrame(() => {
      const item = workspaceItemRefs.current.get(selectedWorkspaceId);
      if (!item) return;
      item.scrollIntoView({ block: "nearest" });
      hasScrolledToSelectedOnOpenRef.current = true;
    });
  }, [isDropdownOpen, selectedWorkspaceId, workspaceDropdownRect]);

  // Loading states
  // - Workspaces loading: blocks the dropdown because we don't have options yet.
  // - Orders loading: only affects "already hired" exclusion; we can still allow selecting a workspace while this loads.
  // When serviceId exists, we may be loading both filtered and all-workspaces (fallback).
  const isWorkspacesLoading = serviceId
    ? Boolean(isLoadingFiltered)
    : Boolean(isLoadingWorkspaces);
  const isEligibilityLoading = Boolean(serviceId && loadingOrders);
  const isWorkspaceSelectLoading = Boolean(isWorkspacesLoading);

  // Initialize selected workspace (do not override user choice)
  useEffect(() => {
    // If we previously forced create-mode while data was loading,
    // automatically return to selection once options are available,
    // but don't interrupt if the user already started typing.
    if (isCreatingWorkspace) {
      // If user intentionally entered create-mode, never auto-bounce back to selection.
      if (!wasAutoForcedCreateRef.current) return;
      if (newWorkspaceName.trim() !== "") return;
      if (selectableWorkspaces.length === 0) return;
      wasAutoForcedCreateRef.current = false;
      setIsCreatingWorkspace(false);
      return;
    }

    const isWorkspaceIdSelectable =
      Boolean(workspaceId) &&
      selectableWorkspaces.some((w) => w.id === workspaceId);

    // Prefer prop workspaceId only if it's selectable for this service.
    if (
      workspaceId &&
      isWorkspaceIdSelectable &&
      selectedWorkspaceId !== workspaceId &&
      !hasUserSelectedWorkspaceRef.current &&
      !hiredWorkspaceIdSet.has(workspaceId)
    ) {
      setSelectedWorkspaceId(workspaceId);
      return;
    }

    if (selectableWorkspaces.length > 0 && !selectedWorkspaceId) {
      setSelectedWorkspaceId(selectableWorkspaces[0].id);
      setIsCreatingWorkspace(false); // Valid workspace found, default to select
      return;
    }

    // Force create-mode when there are no selectable workspaces.
    // If we're in a service context (filtered endpoint), an empty list means user must create a new workspace,
    // even if they arrived with a workspaceId in the URL.
    const shouldForceCreate =
      selectableWorkspaces.length === 0 &&
      !isWorkspacesLoading &&
      !isEligibilityLoading &&
      (serviceId ? true : !workspaceId || hiredWorkspaceIdSet.has(workspaceId));

    if (shouldForceCreate) {
      wasAutoForcedCreateRef.current = true;
      setIsCreatingWorkspace(true); // No selectable workspaces, force create
      setNewWorkspaceName("");
    }
  }, [
    workspaceId,
    serviceId,
    isWorkspacesLoading,
    isEligibilityLoading,
    selectedWorkspaceId,
    isCreatingWorkspace,
    selectableWorkspaces,
    hiredWorkspaceIdSet,
    newWorkspaceName,
  ]);

  // Keep `selectedWorkspaceId` consistent with what is actually selectable in the dropdown.
  useEffect(() => {
    if (isCreatingWorkspace) return;
    if (!selectedWorkspaceId) return;

    const isSelectable = selectableWorkspaces.some(
      (w) => w.id === selectedWorkspaceId,
    );
    if (isSelectable) return;

    if (selectableWorkspaces.length > 0) {
      setSelectedWorkspaceId(selectableWorkspaces[0].id);
    } else {
      setSelectedWorkspaceId("");
    }
  }, [isCreatingWorkspace, selectedWorkspaceId, selectableWorkspaces]);

  // Ensure workspaces are loaded (global). This modal can fall back to all workspaces,
  // so make sure they're fetched when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    if (allWorkspaces.length === 0) {
      fetchWorkspaces();
    }
  }, [isOpen, allWorkspaces.length, fetchWorkspaces]);

  // Fetch filtered workspaces if serviceId is present
  useEffect(() => {
    const loadFilteredWorkspaces = async () => {
      if (serviceId) {
        setIsLoadingFiltered(true);
        try {
          const data = (await workspacesApi.filterByService(serviceId)) as
            | Workspace[]
            | { data?: Workspace[]; workspaces?: Workspace[] };

          // Handle response structure
          let list: Workspace[] = [];
          if (Array.isArray(data)) {
            list = data;
          } else if (data && Array.isArray(data.data)) {
            list = data.data;
          } else if (data && Array.isArray(data.workspaces)) {
            list = data.workspaces;
          }

          setFilteredWorkspaces(list);

          // Selection + create-mode is handled by the initializer effect (it also respects hire status).
        } catch (error) {
          console.error("Failed to filter workspaces", error);
          // Fallback to empty list
          setFilteredWorkspaces([]);
        } finally {
          setIsLoadingFiltered(false);
        }
      }
    };

    if (serviceId) {
      loadFilteredWorkspaces();
    } else {
      // If no serviceId, we use allWorkspaces (handled by derived variable),
      // but we might want to sync filteredWorkspaces to empty to avoid confusion if serviceId is removed dynamically
      setFilteredWorkspaces([]);
    }
  }, [serviceId]);

  const handleConfirm = async () => {
    setIsProcessing(true);
    // Initialize finalWorkspaceId here so it's available in the finally block / end of try
    let finalWorkspaceId: string | undefined = workspaceId;

    try {
      let createdOrderId: string | undefined;

      // Create order via API if serviceId and packageId are provided
      if (serviceId && packageId) {
        // Build order data, only include workspaceId if it's defined
        const orderData: {
          serviceId: string;
          packageId: string;
          taskTitle: string;
          taskDescription: string;
          notes?: string;
          workspaceId?: string;
        } = {
          serviceId,
          packageId,
          taskTitle: `Hire ${workerData.title} - ตอบทันที`,
          taskDescription: `Hired ${workerData.title} - ตอบทันที`,
          notes: `Price: ${workerData.price}`,
        };

        // Remove local declaration, use the one from outer scope
        // let finalWorkspaceId = workspaceId;

        // Handle workspace creation or selection
        // If user selected a workspace in the UI, prefer that over the incoming prop workspaceId.
        // (The prop is often derived from the current page/URL; it should not silently override the user's choice.)
        if (!isCreatingWorkspace && selectedWorkspaceId) {
          finalWorkspaceId = selectedWorkspaceId;
        } else if (isCreatingWorkspace && newWorkspaceName.trim()) {
          try {
            let imageUrl: string | undefined;
            if (workspaceAvatar.mode === "image" && workspaceAvatar.file) {
              try {
                const uploadRes = (await uploadApi.uploadImage(
                  workspaceAvatar.file,
                  undefined,
                  "thumbnails",
                )) as {
                  url?: string;
                  imageUrl?: string;
                  file?: { url?: string };
                };
                imageUrl =
                  uploadRes.url ||
                  uploadRes.imageUrl ||
                  uploadRes.file?.url ||
                  undefined;
              } catch (uploadErr) {
                console.error("Failed to upload workspace image:", uploadErr);
              }
            }

            const createImageUrl = resolveWorkspaceImageUrlForCreate(
              workspaceAvatar,
              imageUrl,
            );
            const newWorkspace = (await workspacesApi.create({
              name: newWorkspaceName,
              ...(createImageUrl ? { imageUrl: createImageUrl } : {}),
            })) as { id?: string; workspace?: { id?: string } };

            const createdId = newWorkspace?.id || newWorkspace?.workspace?.id;
            if (createdId) {
              finalWorkspaceId = createdId;
              try {
                await syncWorkspaceAvatarTags(createdId, workspaceAvatar, []);
              } catch (tagErr) {
                console.warn("Failed to set workspace avatar tags", tagErr);
              }
              fetchWorkspaces();
              if (onWorkspaceCreated) onWorkspaceCreated();
            }
          } catch (err) {
            console.error("Failed to create workspace:", err);
            // Fallback or error handling
          }
        }

        // Only add workspaceId if it's defined
        if (finalWorkspaceId) {
          orderData.workspaceId = finalWorkspaceId;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await api.orders.create(orderData);

        if (response?.order?.id) {
          createdOrderId = response.order.id;
        } else if (response?.id) {
          createdOrderId = response.id;
        }

        // If payment is free, update status to paid immediately
        if (paymentMethod === "free" && createdOrderId) {
          await api.orders.update(createdOrderId, { status: "paid" });
        }

        // Refresh orders list for the workspace that just bought this service.
        await fetchOrders("buyer", finalWorkspaceId);

        // Notify parent window about the new order (for embedded view sync)
        if (typeof window !== "undefined") {
          const message = {
            type: "AERA_ORDER_CREATED",
            orderId: createdOrderId,
            serviceId,
            workspaceId: finalWorkspaceId,
          };
          window.parent.postMessage(message, "*");
          // Also dispatch locally in case we are in the same window context
          window.postMessage(message, "*");
        }
      }

      // Simulate processing delay
      if (paymentMethod !== "free") {
        await new Promise((resolve) => setTimeout(resolve, 1500));
      } else {
        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      // Navigate to services page with query parameters
      // Removed internal navigation to let parent handle it via onConfirm

      // Determine workspace name for auto-fill
      let finalWorkspaceName: string | undefined;
      if (newWorkspaceName.trim()) {
        finalWorkspaceName = newWorkspaceName;
      } else if (finalWorkspaceId) {
        const ws = workspaces.find((w) => w.id === finalWorkspaceId);
        if (ws) finalWorkspaceName = ws.name;
      }

      onConfirm(createdOrderId, finalWorkspaceId, finalWorkspaceName);
      onClose();
    } catch (error) {
      console.error("Confirmation failed:", error);
      alert("Failed to create order. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!mounted) return null;

  const footerButtons = (
    <div className="flex gap-3 w-full">
      <AeraButton
        variant="outline"
        fontWeight="medium"
        onClick={onClose}
        disabled={isProcessing}
        className="flex-1"
      >
        ยกเลิก
      </AeraButton>
      <AeraButton
        variant="primary"
        fontWeight="medium"
        onClick={handleConfirm}
        loading={isProcessing}
        disabled={
          isProcessing ||
          (mustCreateNewWorkspace
            ? !newWorkspaceName.trim()
            : isCreatingWorkspace
              ? !newWorkspaceName.trim()
              : !workspaceId && !selectedWorkspaceId)
        }
        className="flex-1"
      >
        {isProcessing ? "กำลังประมวลผล..." : "ยืนยันการจ้าง (ฟรี)"}
      </AeraButton>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onExited={onExited}
      title="ยืนยันการจ้าง"
      footer={footerButtons}
      size="md"
    >
      <div className="flex flex-col items-center text-center">
        {/* Hero Icon */}
        <div className="w-16 h-16 bg-[#FEF3C7] rounded-3xl flex items-center justify-center mb-6 text-[#D97706]">
          <ShieldCheck size={28} strokeWidth={2.5} />
        </div>

        <p className="text-aera-charcoal mb-8 text-[15px] leading-relaxed">
          คุณกำลังเพิ่ม{" "}
          <span className="font-bold text-aera-charcoal">
            {workerData.title}
          </span>{" "}
          เข้าไปในทีมงานของคุณ.
        </p>

        {/* Workspace Selection */}
        <div className="w-full mb-6 text-left">
          {isProcessing ? (
            <div className="animate-pulse">
              <div className="flex items-center justify-between mb-3">
                <div className="h-3 w-32 bg-aera-lightgray rounded" />
                <div className="h-6 w-24 bg-aera-lightgray rounded-lg" />
              </div>

              <div className="w-full bg-aera-white border border-aera-lightgray/80 rounded-xl px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-3 w-full">
                  <div className="w-6.5 h-6.5 rounded-lg bg-aera-lightgray shrink-0" />
                  <div className="h-4 w-40 max-w-[60%] bg-aera-lightgray rounded" />
                </div>
                <div className="h-4 w-4 bg-aera-lightgray rounded shrink-0" />
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-aera-amber uppercase tracking-wider pl-1">
                  {!isCreatingWorkspace ? "เลือกเวิร์กสเปซ" : "สร้างใหม่"}
                </label>

                <AeraButton
                  variant="primary"
                  size="sm"
                  type="button"
                  fontWeight="medium"
                  onClick={() => {
                    if (isCreatingWorkspace) {
                      wasAutoForcedCreateRef.current = false;
                      setIsCreatingWorkspace(false);
                      if (!selectedWorkspaceId) {
                        const isWorkspaceIdSelectable =
                          Boolean(workspaceId) &&
                          selectableWorkspaces.some(
                            (w) => w.id === workspaceId,
                          );

                        if (
                          workspaceId &&
                          isWorkspaceIdSelectable &&
                          !hiredWorkspaceIdSet.has(workspaceId)
                        ) {
                          setSelectedWorkspaceId(workspaceId);
                        } else if (selectableWorkspaces.length > 0) {
                          setSelectedWorkspaceId(selectableWorkspaces[0].id);
                        }
                      }
                    } else {
                      wasAutoForcedCreateRef.current = false;
                      setIsCreatingWorkspace(true);
                      setIsDropdownOpen(false);
                      setSelectedWorkspaceId("");
                      setNewWorkspaceName("");
                      setWorkspaceAvatar(DEFAULT_WORKSPACE_AVATAR);
                    }
                  }}
                  disabled={
                    isProcessing ||
                    isWorkspaceSelectLoading ||
                    (isCreatingWorkspace && mustCreateNewWorkspace)
                  }
                >
                  {isCreatingWorkspace ? (
                    <>
                      <Check size={14} />
                      เลือก
                    </>
                  ) : (
                    <>
                      <Plus size={14} />
                      สร้างใหม่
                    </>
                  )}
                </AeraButton>
              </div>

              {!isCreatingWorkspace ? (
                <div className="relative">
                  <AeraButton
                    variant="outline"
                    size="md"
                    type="button"
                    fullWidth
                    className="px-[11px]!"
                    ref={workspaceButtonRef}
                    onClick={() => setIsDropdownOpen((v) => !v)}
                    disabled={isProcessing || isWorkspaceSelectLoading}
                    iconRight={<Building size={16} />}
                  >
                    <div className="w-[calc(100%-25px)] flex items-center gap-2">
                      {isWorkspaceSelectLoading ? (
                        <div className="flex items-center gap-3 w-full">
                          <div className="w-6.5 h-6.5 rounded-lg bg-aera-lightgray animate-pulse shrink-0" />
                          <div className="h-4 w-40 max-w-[60%] bg-aera-lightgray rounded animate-pulse" />
                        </div>
                      ) : selectedWorkspaceId ? (
                        (() => {
                          const ws = enrichWorkspace(
                            selectableWorkspaces.find(
                              (w) => w.id === selectedWorkspaceId,
                            ) ||
                              workspaces.find(
                                (w) => w.id === selectedWorkspaceId,
                              ) ||
                              allWorkspaces.find(
                                (w) => w.id === selectedWorkspaceId,
                              ) || { id: selectedWorkspaceId, name: "" },
                          );
                          return (
                            <>
                              <HireModalWorkspaceAvatar
                                workspace={ws}
                                size={26}
                                iconSize={12}
                              />
                              <span className="font-medium truncate">
                                {ws?.name || "เลือกเวิร์กสเปซ..."}
                              </span>
                            </>
                          );
                        })()
                      ) : (
                        <span className="text-aera-gray">
                          เลือกเวิร์กสเปซ...
                        </span>
                      )}
                    </div>
                  </AeraButton>

                  {mounted &&
                    isDropdownOpen &&
                    workspaceDropdownRect &&
                    typeof document !== "undefined" &&
                    createPortal(
                      <div
                        role="listbox"
                        aria-label="Select workspace"
                        ref={workspaceDropdownRef}
                        className="fixed z-9999 flex max-h-60 flex-col overflow-hidden p-2 bg-aera-white border border-aera-lightgray/80 rounded-xl shadow-xl animate-in fade-in zoom-in-95 duration-100"
                        style={{
                          top: workspaceDropdownRect.top,
                          left: workspaceDropdownRect.left,
                          width: workspaceDropdownRect.width,
                        }}
                      >
                        {isWorkspaceSelectLoading ? (
                          <div className="px-4 py-2">
                            {[0, 1, 2].map((i) => (
                              <div
                                key={i}
                                className="flex items-center gap-3 px-0 py-2.5"
                              >
                                <div className="w-8 h-8 rounded-lg bg-aera-lightgray animate-pulse shrink-0" />
                                <div className="h-4 w-40 max-w-[70%] bg-aera-lightgray rounded animate-pulse" />
                              </div>
                            ))}
                          </div>
                        ) : selectableWorkspaces.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-aera-gray text-center">
                            ไม่พบเวิร์กสเปซ
                          </div>
                        ) : (
                          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] flex flex-col gap-1">
                            {selectableWorkspaces.map((ws) => {
                              const displayWs = enrichWorkspace(ws);
                              const isSelected = selectedWorkspaceId === ws.id;
                              return (
                                <AeraButton
                                  key={ws.id}
                                  ref={(node) => {
                                    if (node) {
                                      workspaceItemRefs.current.set(ws.id, node);
                                      return;
                                    }
                                    workspaceItemRefs.current.delete(ws.id);
                                  }}
                                  variant="ghost"
                                  size="md"
                                  fullWidth
                                  type="button"
                                  fontWeight="medium"
                                  paddingSize="xxs"
                                  onClick={() => {
                                    hasUserSelectedWorkspaceRef.current = true;
                                    setSelectedWorkspaceId(ws.id);
                                    setIsDropdownOpen(false);
                                  }}
                                  className={`text-aera-charcoal! ${isSelected ? "bg-aera-lightgray/40!" : "hover:bg-aera-lightgray/40!"}`}
                                >
                                  <HireModalWorkspaceAvatar
                                    workspace={displayWs}
                                    size={32}
                                    iconSize={14}
                                  />
                                  <span className="text-left truncate flex-1">
                                    {ws.name}
                                  </span>
                                  {isSelected ? (
                                    <Check
                                      size={16}
                                      strokeWidth={3}
                                      className="shrink-0 text-aera-amber"
                                      aria-hidden
                                    />
                                  ) : (
                                    <span className="h-4 w-4 shrink-0" aria-hidden />
                                  )}
                                </AeraButton>
                              );
                            })}
                          </div>
                        )}
                      </div>,
                      document.body,
                    )}
                </div>
              ) : (
                <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                  <WorkspaceNameIconInput
                    name={newWorkspaceName}
                    onNameChange={setNewWorkspaceName}
                    avatar={workspaceAvatar}
                    onAvatarChange={setWorkspaceAvatar}
                    autoFocus
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Pricing Box — packages.price / packages.dailyPrice (เดียวกับ PricingBar) */}
        <div className="w-full bg-aera-offwhite border border-aera-lightgray/80 rounded-2xl p-5 mb-6">
          <div className="flex justify-between items-center mb-3">
            <span className="text-aera-gray font-medium text-sm">ค่าจ้าง</span>
            <div className="flex items-center gap-2">
              {showStrikethroughHiringFee && strikethroughHiringFee && (
                <span className="text-aera-gray line-through text-xs tabular-nums">
                  {strikethroughHiringFee}
                </span>
              )}
              <span className="font-bold text-aera-charcoal tabular-nums">
                {pricingLoading
                  ? "—"
                  : pricingReady
                    ? hiringFee.toFixed(2)
                    : "—"}
              </span>
              <span className="text-[11px] font-medium text-aera-gray">
                เครดิต
              </span>
            </div>
          </div>
          <div className="flex justify-between items-center mb-4">
            <span className="text-aera-gray font-medium text-sm">
              ค่าจ้างรายวัน
            </span>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {showStrikethroughDailyPrice && strikethroughDailyValue && (
                <span className="text-aera-gray line-through text-xs tabular-nums">
                  {strikethroughDailyValue}
                </span>
              )}
              {packagePrice === 0 && dailyRetainer === 0 && (
                <span className="bg-[#FEF3C7] text-aera-amber text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-aera-greendark" />
                  ฟรี
                </span>
              )}
              <span className="font-bold text-aera-charcoal tabular-nums">
                {pricingLoading
                  ? "—"
                  : pricingReady
                    ? dailyRetainer.toFixed(2)
                    : "—"}
              </span>
              <span className="text-[11px] font-medium text-aera-gray">
                เครดิต
              </span>
            </div>
          </div>

          <div className="border-t border-aera-lightgray border-dashed my-3" />

          <div className="flex justify-between items-center">
            <span className="font-bold text-aera-charcoal text-sm">
              รวมค่าจ้างรายวัน
            </span>
            <div className="flex items-baseline gap-1.5">
              <span className="font-bold text-[#D97706] text-xl tabular-nums">
                {pricingLoading
                  ? "—"
                  : pricingReady
                    ? totalDueToday.toFixed(2)
                    : "—"}
              </span>
              <span className="text-xs font-semibold text-[#D97706]/80">
                เครดิต
              </span>
            </div>
          </div>
        </div>

        {/* Notice */}
        <div className="w-full bg-[#FEF3C7] text-aera-charcoal rounded-xl p-3 flex items-start gap-3 text-left mb-8">
          <Info size={16} className="shrink-0 mt-0.5 text-aera-amberdark" />
          <p className="text-xs leading-5 font-medium">
            <span className="font-bold">Beta Grant Applied:</span>
            โดยจะครอบคลุมค่าธรรมเนียมการจ้างงานและค่าจ้างให้แบบ 100%
          </p>
        </div>

        {/* Footer Note */}
        <div className="flex items-center justify-center gap-2 text-[#94A3B8] text-xs font-medium w-full">
          <Shield size={14} />
          <span>ไม่ต้องมีบัตรเครดิต.</span>
        </div>
      </div>
    </Modal>
  );
}
